import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";

/**
 * Unified pagination footer (Pagination Footer design — Option C):
 * a single bar at the bottom of a table card with page-size selector,
 * "Showing X–Y of TOTAL" range, and a «‹ Page X of Y ›» stepper.
 */
@Component({
  imports: [CommonModule, TranslateModule],
  selector: "app-pagination-footer",
  standalone: true,
  styleUrls: ["./pagination-footer.component.scss"],
  templateUrl: "./pagination-footer.component.html"
})
export class PaginationFooterComponent {
  @Input() public total: number = 0;
  @Input() public pages: number = 1;
  @Input() public currentPage: number = 1;
  @Input() public currentPageSize: number = 10;
  @Input() public isEstimate: boolean = false;
  @Input() public countOnly: boolean = false;
  @Input() public pageSizeOptions: number[] = [10, 20, 50, 100];

  @Output() public setCurrentPage: EventEmitter<number> = new EventEmitter<number>();
  @Output() public setPageSize: EventEmitter<number> = new EventEmitter<number>();

  // Inputs may arrive as strings from the API/template — coerce before any math
  // so "1" + 1 doesn't become "11".
  private get pageNum(): number {
    return Number(this.currentPage) || 1;
  }

  private get pagesNum(): number {
    return Number(this.pages) || 1;
  }

  public get sizeNum(): number {
    return Number(this.currentPageSize) || 1;
  }

  public get rangeStart(): number {
    return this.total ? (this.pageNum - 1) * this.sizeNum + 1 : 0;
  }

  public get rangeEnd(): number {
    return Math.min(Number(this.total) || 0, this.pageNum * this.sizeNum);
  }

  public get atStart(): boolean {
    return this.pageNum <= 1;
  }

  public get atEnd(): boolean {
    return this.pageNum >= this.pagesNum;
  }

  public first(): void {
    if (!this.atStart) {
      this.setCurrentPage.emit(1);
    }
  }

  public prev(): void {
    if (!this.atStart) {
      this.setCurrentPage.emit(this.pageNum - 1);
    }
  }

  public next(): void {
    if (!this.atEnd) {
      this.setCurrentPage.emit(this.pageNum + 1);
    }
  }

  public last(): void {
    if (!this.atEnd) {
      this.setCurrentPage.emit(this.pagesNum);
    }
  }

  public onSize(event: Event): void {
    this.setPageSize.emit(Number((event.target as HTMLSelectElement).value));
  }
}
